import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { axiosInstance as axios } from '../context/AuthContext';
import { FaArrowLeft, FaCamera, FaCalendarAlt, FaFolder, FaFilter, FaTimes, FaTh, FaClock, FaPlus, FaUpload, FaVideo, FaTrash } from 'react-icons/fa';
import { PhotoLightbox, PhotoUpload } from '../components/PhotoGallery';
import { toast } from 'sonner';

import { TimelineView, GridView, AlbumsView, TrashView, CreateAlbumModal, ShareAlbumModal } from '../components/gallery';
import { chunkedUpload } from '../utils/chunkedUpload';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

function FamilyGallery() {
  const { familyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [galleryData, setGalleryData] = useState(null);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [filter, setFilter] = useState('all');
  const [filterOptions, setFilterOptions] = useState({ events: [], albums: [] });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [viewMode, setViewMode] = useState('timeline');
  
  // Album management
  const [albums, setAlbums] = useState([]);
  const [showCreateAlbumModal, setShowCreateAlbumModal] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState('');
  const [newAlbumDescription, setNewAlbumDescription] = useState('');
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingAlbum, setSharingAlbum] = useState(null);
  const [shareLink, setShareLink] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  
  // Direct upload state
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [familyEvents, setFamilyEvents] = useState([]);
  const [uploadAlbumId, setUploadAlbumId] = useState(null);
  const [uploadAlbumName, setUploadAlbumName] = useState('');

  // Trash (soft-deleted photos)
  const [deletedPhotos, setDeletedPhotos] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [trashError, setTrashError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [purgingId, setPurgingId] = useState(null);

  const fetchDeletedPhotos = async () => {
    setLoadingTrash(true);
    setTrashError(null);
    try {
      const res = await axios.get(`${API}/families/${familyId}/photos/deleted`, { withCredentials: true });
      setDeletedPhotos(res.data || []);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Could not load trash';
      setTrashError(msg);
      setDeletedPhotos([]);
    } finally {
      setLoadingTrash(false);
    }
  };

  const handleRestorePhoto = async (photoId) => {
    setRestoringId(photoId);
    try {
      await axios.post(`${API}/photos/${photoId}/restore`, {}, { withCredentials: true });
      toast.success('Photo restored');
      setDeletedPhotos(prev => prev.filter(p => p.photo_id !== photoId));
      fetchGalleryData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to restore photo');
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentDelete = async (photoId) => {
    if (!window.confirm('Permanently delete this photo? This cannot be undone.')) return;
    setPurgingId(photoId);
    try {
      await axios.delete(`${API}/photos/${photoId}/permanent`, { withCredentials: true });
      toast.success('Photo permanently deleted');
      setDeletedPhotos(prev => prev.filter(p => p.photo_id !== photoId));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete photo');
    } finally {
      setPurgingId(null);
    }
  };

  useEffect(() => { fetchGalleryData(); fetchAlbums(); fetchEvents(); }, [familyId]);
  useEffect(() => { if (galleryData) applyFilter(); }, [galleryData, filter]);

  // Safety net: prevent Safari from navigating to a file URL when a photo is
  // dropped outside an explicit drop zone (which would show "Safari can't open the page").
  useEffect(() => {
    const preventDefault = (e) => {
      // Only intercept actual file drags
      if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) {
        e.preventDefault();
      }
    };
    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);
    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);

  // Lazy-load the trash when user switches into it
  useEffect(() => {
    if (viewMode === 'trash') fetchDeletedPhotos();
  }, [viewMode, familyId]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/families/${familyId}/events`, { withCredentials: true });
      const evts = Array.isArray(response.data) ? response.data : response.data.events || [];
      setFamilyEvents(evts);
    } catch (error) { console.error('Error fetching events:', error); }
  };

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API}/families/${familyId}/albums`, { withCredentials: true });
      setAlbums(response.data || []);
    } catch (error) { console.error('Error fetching albums:', error); }
  };

  const handleCreateAlbum = async () => {
    if (!newAlbumName.trim()) return;
    setCreatingAlbum(true);
    try {
      const response = await axios.post(`${API}/albums?family_id=${familyId}&name=${encodeURIComponent(newAlbumName)}&description=${encodeURIComponent(newAlbumDescription)}`, {}, { withCredentials: true });
      toast.success('Album created successfully!');
      setAlbums([...albums, response.data]);
      setShowCreateAlbumModal(false);
      setNewAlbumName('');
      setNewAlbumDescription('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create album');
    } finally { setCreatingAlbum(false); }
  };

  const handleShareAlbum = async (album) => {
    setSharingAlbum(album);
    setShareLink('');
    setShareCopied(false);
    setShowShareModal(true);
    try {
      const response = await axios.post(`${API}/albums/${album.album_id}/share?allow_download=true&expires_days=7`, {}, { withCredentials: true });
      setShareLink(`${window.location.origin}${response.data.share_url}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create share link');
      setShowShareModal(false);
    }
  };

  // "Save All to Photos" — fetch every photo in the album as a blob and pass
  // them all to the native share sheet. iOS Safari 15+ shows a "Save N Images"
  // option that saves them to the Photos app in one tap. Falls back gracefully
  // if browser doesn't support multi-file share.
  const handleSaveAlbumToPhotos = async (album) => {
    if (!navigator.canShare) {
      toast.error('Your browser does not support saving to Photos. Try Safari on iOS or Chrome on Android.');
      return;
    }
    try {
      // Get the list of photos in this album
      const photosRes = await axios.get(`${API}/albums/${album.album_id}/photos`, { withCredentials: true });
      const albumPhotos = (photosRes.data || []).filter(p => p.media_type !== 'video' && !(p.content_type || '').startsWith('video/'));

      if (albumPhotos.length === 0) {
        toast.info('This album has no photos to save (videos are skipped).');
        return;
      }

      // Hard cap to keep things sane — iOS share sheet struggles past ~25 items
      const MAX = 25;
      const subset = albumPhotos.slice(0, MAX);
      if (albumPhotos.length > MAX) {
        toast.info(`Sending the first ${MAX} photos. Repeat for the rest.`);
      } else {
        toast.info(`Preparing ${subset.length} photo${subset.length === 1 ? '' : 's'}…`);
      }

      const token = localStorage.getItem('session_token');
      const files = [];
      let failed = 0;
      for (let i = 0; i < subset.length; i++) {
        const photo = subset[i];
        try {
          const res = await fetch(`${API}/photos/${photo.photo_id}/download`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!res.ok) { failed += 1; continue; }
          const blob = await res.blob();
          const filename = `${album.name.replace(/[^a-z0-9_-]+/gi, '_')}_${i + 1}.jpg`;
          files.push(new File([blob], filename, { type: blob.type || 'image/jpeg' }));
        } catch (err) {
          console.error('Could not fetch photo', photo.photo_id, err);
          failed += 1;
        }
      }

      if (files.length === 0) {
        toast.error('Could not load any photos. Please try again.');
        return;
      }

      if (!navigator.canShare({ files })) {
        toast.error('This device cannot share multiple photos at once. Try opening each photo and using "Save to Photos" individually.');
        return;
      }

      try {
        await navigator.share({ files, title: album.name });
        toast.success(`Tap "Save ${files.length} Images" in the share menu to add them to Photos${failed > 0 ? ` (${failed} skipped)` : ''}`);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        throw err;
      }
    } catch (error) {
      console.error('Save album to photos error:', error);
      toast.error('Could not save album to Photos. Please try saving photos one at a time.');
    }
  };

  // Download an album as a ZIP file (works on every browser/desktop).
  // Fetches each photo blob, bundles via JSZip, and triggers a single download.
  const handleDownloadAlbumZip = async (album) => {
    try {
      const photosRes = await axios.get(`${API}/albums/${album.album_id}/photos`, { withCredentials: true });
      const albumPhotos = photosRes.data || [];

      if (albumPhotos.length === 0) {
        toast.info('This album has no photos to download.');
        return;
      }

      toast.info(`Building ZIP of ${albumPhotos.length} item${albumPhotos.length === 1 ? '' : 's'}…`);

      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      const token = localStorage.getItem('session_token');
      const safeAlbumName = album.name.replace(/[^a-z0-9_-]+/gi, '_');
      const usedNames = new Set();
      let failed = 0;

      for (let i = 0; i < albumPhotos.length; i++) {
        const photo = albumPhotos[i];
        try {
          const res = await fetch(`${API}/photos/${photo.photo_id}/download`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!res.ok) { failed += 1; continue; }
          const blob = await res.blob();

          // Prefer original filename; fall back to a generated one
          let filename = `${safeAlbumName}_${String(i + 1).padStart(3, '0')}`;
          const cd = res.headers.get('Content-Disposition');
          if (cd) {
            const match = cd.match(/filename="?([^"]+)"?/);
            if (match) filename = match[1];
          } else {
            // Guess extension from MIME
            const ext = (blob.type.split('/')[1] || 'jpg').split(';')[0];
            filename = `${filename}.${ext}`;
          }
          // De-dupe names
          let unique = filename;
          let n = 1;
          while (usedNames.has(unique)) {
            const dot = filename.lastIndexOf('.');
            unique = dot > 0
              ? `${filename.slice(0, dot)}_${n}${filename.slice(dot)}`
              : `${filename}_${n}`;
            n += 1;
          }
          usedNames.add(unique);

          zip.file(unique, blob);
        } catch (err) {
          console.error('ZIP fetch failed for', photo.photo_id, err);
          failed += 1;
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' }, (meta) => {
        // Could surface compression progress here; toast spam isn't worth it.
        if (meta.percent >= 100) { /* done */ }
      });

      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeAlbumName || 'album'}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(
        failed > 0
          ? `Downloaded ${albumPhotos.length - failed} of ${albumPhotos.length} items (${failed} skipped).`
          : `Downloaded ${albumPhotos.length} item${albumPhotos.length === 1 ? '' : 's'}.`
      );
    } catch (error) {
      console.error('Download album ZIP error:', error);
      toast.error('Could not build the ZIP. Please try again.');
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setShareCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setShareCopied(false), 3000);
    } catch (error) { toast.error('Failed to copy link'); }
  };

  // Direct upload to gallery — uses chunked upload for large files
  const handleDirectUpload = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    try {
      const isSmall = file.size < 5 * 1024 * 1024; // Under 5MB, use simple upload

      if (isSmall) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('family_id', familyId);
        if (uploadAlbumId) formData.append('album_id', uploadAlbumId);
        await axios.post(`${API}/photos/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setUploadProgress(100);
      } else {
        // Use chunked upload for larger files
        await chunkedUpload(file, {
          metadata: { family_id: familyId, upload_type: 'gallery', album_id: uploadAlbumId || undefined },
          onProgress: (pct) => setUploadProgress(pct),
        });
      }

      toast.success(`${file.type.startsWith('video/') ? 'Video' : 'Photo'} uploaded${uploadAlbumName ? ` to "${uploadAlbumName}"` : ''} successfully!`);
      setLightboxOpen(false);
      fetchGalleryData();
      fetchAlbums();
      setShowUploadSection(false);
      setUploadAlbumId(null);
      setUploadAlbumName('');
    } catch (error) {
      console.error('Error uploading:', error);
      const message = error.message || error.response?.data?.detail || 'Upload failed. Please try again.';
      toast.error(message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Drag-and-drop a photo directly onto an album card — uploads straight into that album
  const handleDropToAlbum = async (album, rawFile) => {
    if (!rawFile) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadAlbumId(album.album_id);
    setUploadAlbumName(album.name);

    try {
      const { validateMediaFile, isHeicFile, convertHeicToJpeg } = await import('../utils/chunkedUpload');

      // Pre-flight readability check (catches macOS Photos iCloud placeholders)
      const slice = rawFile.slice(0, Math.min(64 * 1024, rawFile.size));
      const readable = await new Promise((resolve) => {
        if (!rawFile.size) return resolve(false);
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result?.byteLength ?? 0) > 0);
        reader.onerror = () => resolve(false);
        try { reader.readAsArrayBuffer(slice); } catch { resolve(false); }
      });
      if (!readable) {
        toast.error("Safari couldn't read this file. If you dragged from the macOS Photos app, drag it to your Desktop first, then drop it onto the album.");
        return;
      }

      // Auto-convert HEIC from iPhone to JPEG
      let file = rawFile;
      if (isHeicFile(rawFile)) {
        toast.info('Converting iPhone photo (HEIC → JPEG)…');
        file = await convertHeicToJpeg(rawFile);
      }

      const result = await validateMediaFile(file);
      if (!result.valid) {
        toast.error(result.error);
        return;
      }

      const isSmall = file.size < 5 * 1024 * 1024;
      if (isSmall) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('family_id', familyId);
        formData.append('album_id', album.album_id);
        await axios.post(`${API}/photos/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setUploadProgress(100);
      } else {
        await chunkedUpload(file, {
          metadata: { family_id: familyId, upload_type: 'gallery', album_id: album.album_id },
          onProgress: (pct) => setUploadProgress(pct),
        });
      }

      toast.success(`${file.type.startsWith('video/') ? 'Video' : 'Photo'} added to "${album.name}"!`);
      fetchGalleryData();
      fetchAlbums();
    } catch (error) {
      console.error('Error dropping to album:', error);
      const message = error.message || error.response?.data?.detail || 'Upload failed. Please try again.';
      toast.error(message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadAlbumId(null);
      setUploadAlbumName('');
    }
  };

  // Open upload section pre-targeted to a specific album
  const handleAddPhotosToAlbum = (album) => {
    setUploadAlbumId(album.album_id);
    setUploadAlbumName(album.name);
    setShowUploadSection(true);
    setViewMode('albums'); // stay on albums view
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteAlbum = async (albumId, albumName) => {
    if (!window.confirm(`Are you sure you want to delete "${albumName}"? Photos will be moved to unassigned.`)) return;
    try {
      await axios.delete(`${API}/albums/${albumId}`, { withCredentials: true });
      toast.success('Album deleted');
      setAlbums(albums.filter(a => a.album_id !== albumId));
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete album'); }
  };

  const fetchGalleryData = async () => {
    try {
      const response = await axios.get(`${API}/families/${familyId}/photos`, { withCredentials: true });
      setGalleryData(response.data);
      const photos = response.data.photos || [];
      const eventsSet = new Map();
      const albumsSet = new Map();
      photos.forEach(photo => {
        if (photo.event_id && photo.event_name) eventsSet.set(photo.event_id, photo.event_name);
        if (photo.album_id && photo.album_name) albumsSet.set(photo.album_id, photo.album_name);
      });
      setFilterOptions({ events: Array.from(eventsSet, ([id, name]) => ({ id, name })), albums: Array.from(albumsSet, ([id, name]) => ({ id, name })) });
      setFilteredPhotos(photos);
    } catch (error) { console.error('Error fetching gallery:', error); }
    finally { setLoading(false); }
  };

  const applyFilter = () => {
    if (!galleryData?.photos) return;
    let photos = galleryData.photos;
    if (filter === 'all') setFilteredPhotos(photos);
    else if (filter === 'events') setFilteredPhotos(photos.filter(p => p.event_id));
    else if (filter === 'albums') setFilteredPhotos(photos.filter(p => p.album_id));
    else if (filter.startsWith('event_')) setFilteredPhotos(photos.filter(p => p.event_id === filter.replace('event_', '')));
    else if (filter.startsWith('album_')) setFilteredPhotos(photos.filter(p => p.album_id === filter.replace('album_', '')));
  };

  const timelineData = useMemo(() => {
    if (!filteredPhotos.length) return { events: [], albums: [], other: [] };
    const eventGroups = new Map();
    const albumGroups = new Map();
    const otherPhotos = [];
    filteredPhotos.forEach(photo => {
      if (photo.event_id) {
        if (!eventGroups.has(photo.event_id)) eventGroups.set(photo.event_id, { id: photo.event_id, name: photo.event_name || 'Unknown Event', date: photo.event_date, endDate: photo.event_end_date, location: photo.event_location, photos: [] });
        eventGroups.get(photo.event_id).photos.push(photo);
      } else if (photo.album_id) {
        if (!albumGroups.has(photo.album_id)) albumGroups.set(photo.album_id, { id: photo.album_id, name: photo.album_name || 'Unknown Album', photos: [] });
        albumGroups.get(photo.album_id).photos.push(photo);
      } else { otherPhotos.push(photo); }
    });
    const events = Array.from(eventGroups.values()).sort((a, b) => { if (!a.date && !b.date) return 0; if (!a.date) return 1; if (!b.date) return -1; return new Date(b.date) - new Date(a.date); });
    return { events, albums: Array.from(albumGroups.values()), other: otherPhotos };
  }, [filteredPhotos]);

  // Create a flat array of photos in the exact order they appear in the timeline view
  const timelineOrderedPhotos = useMemo(() => {
    const ordered = [];
    // First: event photos (in sorted event order)
    timelineData.events.forEach(event => {
      ordered.push(...event.photos);
    });
    // Second: album photos
    timelineData.albums.forEach(album => {
      ordered.push(...album.photos);
    });
    // Third: other photos
    ordered.push(...timelineData.other);
    return ordered;
  }, [timelineData]);

  const openLightbox = (index) => { setLightboxIndex(index); setLightboxOpen(true); };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      await axios.delete(`${API}/photos/${photoId}`, { withCredentials: true });
      toast.success('Photo deleted');
      fetchGalleryData();
      if (filteredPhotos.length <= 1) setLightboxOpen(false);
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Failed to delete photo';
      toast.error(msg);
    }
  };

  const getFilterLabel = () => {
    if (filter === 'all') return 'All Photos';
    if (filter === 'events') return 'Event Photos';
    if (filter === 'albums') return 'Album Photos';
    if (filter.startsWith('event_')) return filterOptions.events.find(e => e.id === filter.replace('event_', ''))?.name || 'Event Photos';
    if (filter.startsWith('album_')) return filterOptions.albums.find(a => a.id === filter.replace('album_', ''))?.name || 'Album Photos';
    return 'All Photos';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading memories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="family-gallery-page">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to={`/family/${familyId}`} className="flex items-center space-x-2 text-gray-600 hover:text-blue-600" data-testid="back-to-family">
                <FaArrowLeft />
                <span>Back to Family</span>
              </Link>
              <span className="text-gray-300">|</span>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{galleryData?.family_name} Memories</h1>
                <p className="text-sm text-gray-600">{galleryData?.total_count || 0} items ({galleryData?.total_photos || 0} photos, {galleryData?.total_videos || 0} videos) - Family Timeline</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Add Photos Button */}
              <button
                onClick={() => setShowUploadSection(!showUploadSection)}
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-xs sm:text-sm"
                data-testid="add-photos-btn"
              >
                <FaPlus />
                <span>Add Media</span>
              </button>
              
              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button onClick={() => setViewMode('timeline')} className={`flex items-center space-x-1 px-2 sm:px-3 py-1.5 rounded-md transition text-xs sm:text-sm ${viewMode === 'timeline' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-800'}`} data-testid="view-timeline">
                  <FaClock /><span>Timeline</span>
                </button>
                <button onClick={() => setViewMode('grid')} className={`flex items-center space-x-1 px-2 sm:px-3 py-1.5 rounded-md transition text-xs sm:text-sm ${viewMode === 'grid' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-800'}`} data-testid="view-grid">
                  <FaTh /><span>Grid</span>
                </button>
                <button onClick={() => setViewMode('albums')} className={`flex items-center space-x-1 px-2 sm:px-3 py-1.5 rounded-md transition text-xs sm:text-sm ${viewMode === 'albums' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-800'}`} data-testid="view-albums">
                  <FaFolder /><span>Albums</span>
                </button>
                <button onClick={() => setViewMode('trash')} className={`flex items-center space-x-1 px-2 sm:px-3 py-1.5 rounded-md transition text-xs sm:text-sm ${viewMode === 'trash' ? 'bg-white shadow text-red-600' : 'text-gray-600 hover:text-gray-800'}`} data-testid="view-trash" title="Recently deleted photos (owner & stewards only)">
                  <FaTrash /><span>Trash</span>
                </button>
              </div>
              
              {/* Filter */}
              <div className="relative">
                <button onClick={() => setShowFilterMenu(!showFilterMenu)} className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs sm:text-sm" data-testid="filter-btn">
                  <FaFilter /><span>{getFilterLabel()}</span>
                </button>
                
                {showFilterMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
                    <div className="p-2">
                      <button onClick={() => { setFilter('all'); setShowFilterMenu(false); }} className={`w-full text-left px-4 py-2 rounded-lg ${filter === 'all' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}>All Photos</button>
                      {filterOptions.events.length > 0 && (
                        <>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-t mt-2 pt-2"><FaCalendarAlt className="inline mr-2" />Events</div>
                          {filterOptions.events.map(event => (
                            <button key={event.id} onClick={() => { setFilter(`event_${event.id}`); setShowFilterMenu(false); }} className={`w-full text-left px-4 py-2 rounded-lg text-sm ${filter === `event_${event.id}` ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}>{event.name}</button>
                          ))}
                        </>
                      )}
                      {filterOptions.albums.length > 0 && (
                        <>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-t mt-2 pt-2"><FaFolder className="inline mr-2" />Albums</div>
                          {filterOptions.albums.map(album => (
                            <button key={album.id} onClick={() => { setFilter(`album_${album.id}`); setShowFilterMenu(false); }} className={`w-full text-left px-4 py-2 rounded-lg text-sm ${filter === `album_${album.id}` ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}>{album.name}</button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Upload Section */}
        {showUploadSection && (
          <div className="mb-6 bg-white rounded-xl shadow-md p-6" data-testid="upload-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FaUpload className="text-blue-600" />
                <h3 className="font-semibold text-gray-800">
                  {uploadAlbumName ? `Upload to "${uploadAlbumName}"` : 'Upload Photos & Videos'}
                </h3>
                {uploadAlbumName && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Album</span>
                )}
              </div>
              <button
                onClick={() => { setShowUploadSection(false); setUploadAlbumId(null); setUploadAlbumName(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            <PhotoUpload 
              onUpload={handleDirectUpload} 
              uploading={uploading}
              uploadProgress={uploadProgress}
            />
            <p className="text-sm text-gray-500 mt-2">
              {uploadAlbumName 
                ? `Photos will be added to the "${uploadAlbumName}" album.`
                : 'Upload photos or videos (up to 200MB / ~5 min). For best results, record in 1080p.'
              }
            </p>
          </div>
        )}

        {filter !== 'all' && (
          <div className="mb-6 flex items-center">
            <span className="text-sm text-gray-600 mr-2">Filtered by:</span>
            <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              {getFilterLabel()}
              <button onClick={() => setFilter('all')} className="ml-2 hover:text-blue-600"><FaTimes /></button>
            </span>
          </div>
        )}

        {viewMode === 'trash' ? (
          <TrashView
            photos={deletedPhotos}
            loading={loadingTrash}
            error={trashError}
            restoringId={restoringId}
            purgingId={purgingId}
            onRestore={handleRestorePhoto}
            onPurge={handlePermanentDelete}
            onBack={() => setViewMode('timeline')}
          />
        ) : viewMode === 'albums' ? (
          <AlbumsView albums={albums} onCreateAlbum={() => setShowCreateAlbumModal(true)} onViewAlbum={(album) => { setFilter(`album_${album.album_id}`); setViewMode('grid'); }} onShareAlbum={handleShareAlbum} onDeleteAlbum={handleDeleteAlbum} onAddPhotos={handleAddPhotosToAlbum} onDropPhoto={handleDropToAlbum} onSaveAlbumToPhotos={handleSaveAlbumToPhotos} onDownloadAlbumZip={handleDownloadAlbumZip} />
        ) : filteredPhotos.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <FaCamera className="text-gray-300 text-6xl mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No memories yet</h3>
            <p className="text-gray-600 mb-4">{filter === 'all' ? "Start capturing family memories by uploading photos to your events!" : "No photos found with the current filter."}</p>
            <Link to={`/family/${familyId}`} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Go to Events</Link>
          </div>
        ) : viewMode === 'timeline' ? (
          <TimelineView timelineData={timelineData} onPhotoClick={openLightbox} />
        ) : (
          <GridView photos={filteredPhotos} onPhotoClick={openLightbox} />
        )}
      </main>

      {/* Modals */}
      <CreateAlbumModal show={showCreateAlbumModal} albumName={newAlbumName} setAlbumName={setNewAlbumName} description={newAlbumDescription} setDescription={setNewAlbumDescription} creating={creatingAlbum} onCreate={handleCreateAlbum} onClose={() => { setShowCreateAlbumModal(false); setNewAlbumName(''); setNewAlbumDescription(''); }} />
      <ShareAlbumModal show={showShareModal} album={sharingAlbum} shareLink={shareLink} copied={shareCopied} onCopy={copyShareLink} onClose={() => { setShowShareModal(false); setSharingAlbum(null); setShareLink(''); }} />

      {/* Lightbox */}
      {lightboxOpen && (
        <PhotoLightbox 
          photos={viewMode === 'timeline' ? timelineOrderedPhotos : filteredPhotos} 
          initialIndex={lightboxIndex} 
          onClose={() => setLightboxOpen(false)} 
          onDelete={handleDeletePhoto} 
          canDelete={true}
          onUpdate={() => { fetchGalleryData(); fetchAlbums(); }}
          events={familyEvents}
          albums={albums}
        />
      )}
    </div>
  );
}

export default FamilyGallery;
